<script setup lang="ts">
import MaterialCover from '../material/MaterialCover.vue';
import CoverFeatures from '../CoverFeatures.vue';
import Menu from '../Menu.vue';
import MdiFaIcon from '../MdiFaIcon.vue';
import { computed, ComputedRef, ref, watch } from 'vue';
import useLocale from '../helpers/use-locale';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { useDisplay } from 'vuetify';
import { ContentWithDuplicates } from '@ilteducation/content';

export type MaterialChip = {
  icon: string;
  title: string;
  theme?: string;
  colorClass?: string;
};

export type ChipMap = Record<string, { icon: string; title: string }>;

const { t } = useLocale();
const { xs } = useDisplay();

type Emits = {
  (e: 'click-download'): void;
  (e: 'click-print'): void;
  (e: 'click-add-to-collection'): void;
  (e: 'language-changed', material: MaterialV1): void;
};

const emit = defineEmits<Emits>();

defineSlots<{
  audience?: (props?: any) => any;
  bookType?: (props?: any) => any;
  categories?: (props?: any) => any;
  after?: (props?: any) => any;
  'audience-ages'?: (props?: any) => any;
  title?: (props?: any) => any;
}>();

type Props = {
  material: MaterialV1;
  previewUrl?: string;
  materialsWithSameOriginalTitle: MaterialV1[];
  isDownloadLoading: boolean;
  isPrintLoading: boolean;
  getLangI18nKey?: (code: string) => string;
  usePadding?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  previewUrl: '',
  getLangI18nKey: (code: string) => `languages.${code}`,
  usePadding: false,
});

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const currentMaterial = ref<MaterialV1>(props.material as MaterialV1);

watch(
  () => props.material,
  (newValue) => {
    if (newValue) {
      currentMaterial.value = newValue as MaterialV1;
    }
  },
  { immediate: true },
);
const mainLanguage = computed(() => ({
  language: capitalize(t(props.getLangI18nKey(currentMaterial.value.language))),
  code: currentMaterial.value.language,
}));

const otherLanguages = computed(() =>
  props.materialsWithSameOriginalTitle
    .map((duplicate: MaterialV1) => ({
      language: capitalize(t(props.getLangI18nKey(duplicate.language))),
      code: duplicate.language,
    }))
    .sort((a, b) => a.language.localeCompare(b.language)),
);

const audienceMap: ChipMap = {
  alone: {
    icon: 'mdi-account',
    title: 'label_audience_alone',
  },
  pair: {
    icon: 'mdi-account-supervisor',
    title: 'label_audience_pair',
  },
  group: {
    icon: 'mdi-account-group',
    title: 'label_audience_group',
  },
  class: {
    icon: 'mdi-town-hall',
    title: 'label_audience_class',
  },
};

const materialChips: ComputedRef<MaterialChip[]> = computed(() => {
  const result: MaterialChip[] = [];
  if (currentMaterial.value.properties?.audiences?.length > 0) {
    currentMaterial.value.properties.audiences.forEach((audience) => {
      if (audienceMap[audience]) {
        result.push(audienceMap[audience]);
      }
    });
  }
  return result;
});

const onLanguageChange = () => {
  const found = props.materialsWithSameOriginalTitle.find(
    (m: MaterialV1) => m.language === mainLanguage.value.code,
  ) as MaterialV1;
  if (found) {
    emit('language-changed', found);
    currentMaterial.value = found;
  }
};
</script>

<template>
  <v-container v-if="material" class="pa-0 material-container bg-surface-container-lowest overflow-y-auto">
    <div class="cover-image">
      <v-fade-transition mode="out-in">
        <div>
          <MaterialCover
            v-if="material"
            :id="currentMaterial.id"
            :preview-url="previewUrl"
            :alt="(currentMaterial as MaterialV1).title"
            :is-rounded="false"
            :height="300"
            aspect-ratio="16/9"
            class="bg-surface-container-highest"
          >
            <CoverFeatures
              class="material-cover-feat"
              :duplicates-count="(material as ContentWithDuplicates).duplicatesCount"
            />
          </MaterialCover>
        </div>
      </v-fade-transition>
    </div>
    <v-container :class="{ 'px-8': usePadding }">
      <div class="d-flex flex-wrap justify-space-between">
        <div class="mr-2 material-select">
          <v-select
            id="material-select"
            v-if="otherLanguages.length > 1"
            v-model="mainLanguage.code"
            class="w-100 my-2"
            :items="otherLanguages"
            color="primary"
            variant="outlined"
            rounded="md"
            density="comfortable"
            item-title="language"
            item-value="code"
            hide-details
            :label="t('label_language')"
            aria-describedby="material-select"
            prepend-inner-icon="mdi-web"
            :menu-props="{ style: 'z-index: 2500' }"
            @update:modelValue="onLanguageChange"
            role="button"
          ></v-select>
        </div>
        <div class="my-1">
          <!-- aria-label is needed since there is otherwise no accessible text on xs screens -->
          <div class="d-flex gc-2">
            <v-btn
              color="primary"
              :icon="xs"
              class="my-1 text-none elevation-0"
              :loading="isPrintLoading"
              @click="emit('click-print')"
              :aria-label="t('button_print')"
            >
              <v-icon :class="{ 'pr-2': !xs }">mdi-printer</v-icon>
              <span v-if="!xs">{{ t('button_print') }}</span>
            </v-btn>
            <v-tooltip location="top" :aria-hidden="true" :disabled="xs">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="text"
                  class="my-1 ml-1 ctrl-btn bg-secondary-container"
                  size="48"
                  color="currentColor"
                  :aria-label="t('components.label_additional')"
                >
                  <MdiFaIcon icon="fa-solid fa-ellipsis" size="medium" />
                  <Menu :open-on-click="true" :title="t('components.label_advanced_menu')">
                    <v-list-item :disabled="isDownloadLoading" @click="emit('click-download')">
                      <v-list-item-title class="menu-item-title">
                        <span class="menu-item-icon">
                          <v-progress-circular v-if="isDownloadLoading" indeterminate size="16" width="2" />
                          <MdiFaIcon v-else icon="mdi-download" />
                        </span>
                        {{ t('button_download') }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="emit('click-add-to-collection')">
                      <v-list-item-title class="menu-item-title">
                        <span class="menu-item-icon">
                          <MdiFaIcon :size="20" icon="fa-regular fa-plus" />
                        </span>
                        {{ t('content_collections.tooltip_add_to_collection') }}
                      </v-list-item-title>
                    </v-list-item>
                  </Menu>
                </v-btn>
              </template>
              {{ t('components.label_additional') }}
            </v-tooltip>
          </div>
        </div>
      </div>
      <div class="mt-2">
        <slot name="title">
          <h2 class="title">{{ (currentMaterial as MaterialV1).title }}</h2>
        </slot>
      </div>
      <div class="mt-4">
        <p class="description" v-html="(currentMaterial as MaterialV1).description"></p>
      </div>
      <div class="mt-4">
        <template v-if="$slots.audience || materialChips.length > 0">
          <p class="mb-4 category-label">{{ t('label_audience') }}</p>
          <ul class="d-flex flex-wrap pa-0 ma-0 chip-list" :aria-label="t('label_audience')">
            <slot name="audience-ages"></slot>
            <v-chip
              v-for="chip in materialChips"
              :key="chip.title"
              class="mr-4 mb-4 bg-secondary-container"
              color="on-surface"
              label
              :prepend-icon="chip.icon"
              variant="flat"
              role="listitem"
              tag="li"
            >
              {{ t(`${chip.title}`) }}
            </v-chip>
          </ul>
        </template>
        <template v-if="$slots.bookType">
          <p class="mb-4 category-label">{{ t('label_book_format') }}</p>
          <ul class="pa-0 ma-0 chip-list" :aria-label="t('label_book_format')">
            <slot name="bookType"></slot>
          </ul>
        </template>
        <template v-if="$slots.categories">
          <p class="mb-4 category-label">{{ t('label_category') }}</p>
          <ul class="pa-0 ma-0 chip-list" :aria-label="t('label_category')">
            <slot name="categories"></slot>
          </ul>
        </template>
        <!-- Slot for additional content after the Categories section -->
        <slot name="after"></slot>
      </div>
    </v-container>
  </v-container>
</template>

<style scoped lang="scss">
.bg-material-cover {
  display: none;
}
.material-container {
  max-width: 800px;
  height: 100%;
}
.cover-image {
  min-height: 150px;
}
:deep(.v-overlay) {
  z-index: 20000 !important;
}
:deep(.v-field__prepend-inner > .v-icon) {
  opacity: 1 !important;
}
.category-label {
  font-size: 12px;
  font-weight: 500;
}
.material-select {
  width: 200px;
}
.title {
  font-size: 1.5rem;
}

.chip-list {
  list-style: none;
}

.menu-item-title {
  display: flex;
  align-items: center;
}

.menu-item-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  font-size: 16px;
  line-height: 1;
}

.description {
  color: rgb(var(--v-theme-on-surface)) !important;

  :deep(*) {
    color: inherit !important;
  }

  :deep(ul) {
    margin-left: 1em;
  }
}
</style>
